/**
 * Component: Browse contents bar, panel and layout
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R30, WIDGET-V1-R29, WIDGET-V1-R28, WIDGET-V1-R21, WIDGET-V1-R22
 *
 * The Browse table of contents while a guideline is open
 * (widget.md 4.11.3). ``BrowseTocLayout`` wraps the guideline body
 * and renders:
 *
 *   - a sticky contents bar (``position: sticky``, top =
 *     ``var(--galen-toc-top-offset, 0px)`` so a deployment can clear a
 *     fixed host header via the ``toc-top-offset`` theme token): a
 *     Contents button plus - only once the bar is stuck - the name of
 *     the top-level section being read;
 *   - the Contents panel - the R29 collapsible tree - opening on the
 *     left under the bar on hover (intent delay) or click, closing
 *     when the pointer leaves;
 *   - when the widget itself is at least 1024 px wide, a pin in the
 *     panel docks it as a sticky column left of the text. Narrower
 *     widgets are offered no pin (there is nowhere to dock).
 *
 * The tree follows the WAI-ARIA navigation treeview pattern (R21):
 * each entry link is the ``treeitem`` (roving tabindex); the chevron
 * is a pointer-only target, so no interactive element is nested in a
 * treeitem. Print exclusion is added through the CSSOM so it survives
 * a strict CSP (R22).
 *
 * Risk Links: R7, R1
 * FMEA Links: FM7, FM2
 */

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DEFAULT_BROWSE_TOC_DEPTH } from '../core/config';
import type { SectionContentEntry } from './browse-content';
import { buildTocTree, type TocTree } from './browse-toc-tree';
import { findScrollContainer } from './scroll-container';
import { useCurrentSection } from './use-current-section';

/* ------------------------------------------------------------------ */
/* Tunables (widget.md 4.11.3)                                         */
/* ------------------------------------------------------------------ */

/** Hover-intent delay before the panel opens. */
export const TOC_OPEN_DELAY_MS = 150;
/** Grace period after the pointer leaves before an unpinned panel closes. */
export const TOC_CLOSE_DELAY_MS = 300;
/** Minimum widget width at which a pinned panel docks beside the text. */
export const TOC_DOCK_MIN_WIDTH_PX = 1024;
/** localStorage key for the per-browser pin preference. */
export const TOC_PIN_STORAGE_KEY = 'galen.browseToc.pinned';

const OFFSET = 'var(--galen-toc-top-offset, 0px)';
const BAR_HEIGHT = '2.75rem';

/* ------------------------------------------------------------------ */
/* Indentation (R28/R29): one scheme for every row                     */
/* ------------------------------------------------------------------ */

const TOC_CHEVRON_COLUMN_REM = 1.75;
const TOC_TEXT_GAP_REM = 0.25;
const TOC_INDENT_STEP_REM = 1;

/**
 * Left padding of an entry's text, given its nesting below the top
 * level. Top-level rows are [chevron column][text], so top-level text
 * is leftmost; sub-level text starts at the same point plus one step
 * per level of real nesting.
 */
export function tocEntryPadding(indent: number): string {
  if (indent <= 0) return `${TOC_TEXT_GAP_REM}rem`;
  // Summed in quarters of a rem to avoid float noise.
  const quarters =
    (TOC_CHEVRON_COLUMN_REM + TOC_TEXT_GAP_REM + TOC_INDENT_STEP_REM * indent) * 4;
  return `${Math.round(quarters) / 4}rem`;
}

/* ------------------------------------------------------------------ */
/* Print exclusion via the CSSOM (R22)                                 */
/* ------------------------------------------------------------------ */

export const TOC_PRINT_CSS =
  '@media print {' +
  ' [data-galen-browse-toc-bar], [data-galen-browse-toc-panel], [data-galen-browse-toc-docked]' +
  ' { display: none !important; }' +
  ' [data-galen-browse-toc-columns] { display: block !important; }' +
  ' }';

const printRuleInstalled = new WeakSet<Document>();

/**
 * Add the print rule once per document. Uses a constructable
 * stylesheet where supported, otherwise ``insertRule`` on an empty
 * ``<style>`` element. Never writes ``<style>`` text, which a CSP
 * without ``'unsafe-inline'`` in ``style-src`` would block.
 */
export function ensureTocPrintRule(doc: Document = document): 'adopted' | 'inserted' | 'none' {
  if (printRuleInstalled.has(doc)) return 'none';
  try {
    if (
      'adoptedStyleSheets' in doc &&
      typeof CSSStyleSheet === 'function' &&
      typeof (CSSStyleSheet.prototype as { replaceSync?: unknown }).replaceSync === 'function'
    ) {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(TOC_PRINT_CSS);
      doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet];
      printRuleInstalled.add(doc);
      return 'adopted';
    }
  } catch {
    /* fall through to insertRule */
  }
  try {
    const el = doc.createElement('style');
    el.setAttribute('data-galen-browse-toc-print', '');
    doc.head.appendChild(el);
    if (!el.sheet) {
      // No stylesheet object (e.g. a document without a window):
      // nothing was installed, so say so rather than claim success.
      el.remove();
      return 'none';
    }
    el.sheet.insertRule(TOC_PRINT_CSS, 0);
    printRuleInstalled.add(doc);
    return 'inserted';
  } catch {
    return 'none';
  }
}

/* ------------------------------------------------------------------ */
/* Pin preference (per browser)                                        */
/* ------------------------------------------------------------------ */

export function readTocPinned(): boolean {
  try {
    return window.localStorage.getItem(TOC_PIN_STORAGE_KEY) === '1';
  } catch {
    return false; // blocked storage degrades to "not pinned"
  }
}

function writeTocPinned(pinned: boolean): void {
  try {
    if (pinned) {
      window.localStorage.setItem(TOC_PIN_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(TOC_PIN_STORAGE_KEY);
    }
  } catch {
    /* blocked storage: preference just isn't remembered */
  }
}

/* ------------------------------------------------------------------ */
/* Widget width (docking)                                              */
/* ------------------------------------------------------------------ */

/** Width of ``ref``'s element, or null before measurement / without ResizeObserver. */
function useElementWidth(ref: React.RefObject<HTMLElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

/* ------------------------------------------------------------------ */
/* Stuck detection (location text)                                     */
/* ------------------------------------------------------------------ */

/**
 * True while the sticky bar is actually stuck. ``sentinelRef`` is a
 * 1px marker placed immediately before the bar: it scrolls normally,
 * so once it has passed the sticky line (the scroll root's top plus
 * the bar's resolved ``top`` offset) the bar is stuck. An
 * IntersectionObserver whose top margin is that offset reports the
 * crossing; no scroll listener. Without IntersectionObserver the bar
 * is treated as never stuck (location text simply stays hidden).
 */
function useStuck(
  sentinelRef: React.RefObject<HTMLElement | null>,
  barRef: React.RefObject<HTMLElement | null>,
): boolean {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const bar = barRef.current;
    if (!sentinel || !bar || typeof IntersectionObserver === 'undefined') return;
    const offset = parseFloat(window.getComputedStyle(bar).top) || 0;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        // rootBounds already include the negative top margin (the offset).
        const line = entry.rootBounds ? entry.rootBounds.top : offset;
        setStuck(!entry.isIntersecting && entry.boundingClientRect.bottom <= line);
      },
      {
        root: findScrollContainer(sentinel),
        rootMargin: `-${offset}px 0px 0px 0px`,
        threshold: [0, 1],
      },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [sentinelRef, barRef]);
  return stuck;
}

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/* ------------------------------------------------------------------ */

const svgBase = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: 'false' as const,
};

const TocChevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    {...svgBase}
    width="16"
    height="16"
    strokeWidth={2.5}
    style={{
      display: 'block',
      transform: open ? 'rotate(90deg)' : 'none',
      transition: 'transform 120ms ease-out',
    }}
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const ListIcon: React.FC = () => (
  <svg {...svgBase} width="16" height="16" strokeWidth={2.25} style={{ display: 'block' }}>
    <line x1="8" y1="6" x2="20" y2="6" />
    <line x1="8" y1="12" x2="20" y2="12" />
    <line x1="8" y1="18" x2="20" y2="18" />
    <circle cx="4" cy="6" r="1" />
    <circle cx="4" cy="12" r="1" />
    <circle cx="4" cy="18" r="1" />
  </svg>
);

const PinIcon: React.FC<{ pinned: boolean }> = ({ pinned }) => (
  <svg
    {...svgBase}
    width="16"
    height="16"
    strokeWidth={2}
    fill={pinned ? 'currentColor' : 'none'}
    style={{ display: 'block' }}
  >
    <path d="M9 4h6l-1 6 3 3v2H7v-2l3-3-1-6z" />
    <line x1="12" y1="15" x2="12" y2="21" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Tree (R29 behaviour, R30 keyboard model)                            */
/* ------------------------------------------------------------------ */

interface TocTreeViewProps {
  tree: TocTree;
  expanded: ReadonlySet<number>;
  onToggle: (rootId: number) => void;
  currentEntryId: number | null;
  onSelect: (sectionId: number, via: 'mouse' | 'touch' | 'keyboard') => void;
  treeRef: React.RefObject<HTMLUListElement | null>;
  idPrefix: string;
}

const TocTreeView: React.FC<TocTreeViewProps> = ({
  tree,
  expanded,
  onToggle,
  currentEntryId,
  onSelect,
  treeRef,
  idPrefix,
}) => {
  const [focusId, setFocusId] = useState<number | null>(null);
  const lastPointer = useRef<'mouse' | 'touch'>('mouse');

  // Visible treeitems in DOM order: roots, plus descendants of open roots.
  const visibleIds = useMemo(() => {
    const ids: number[] = [];
    for (const root of tree.roots) {
      ids.push(root.section.sectionId);
      if (expanded.has(root.section.sectionId)) {
        root.descendants.forEach((d) => ids.push(d.section.sectionId));
      }
    }
    return ids;
  }, [tree, expanded]);

  // Roving tabindex target: last focused if still visible, else the
  // current entry, else the first top-level entry.
  const tabbableId =
    focusId !== null && visibleIds.includes(focusId)
      ? focusId
      : currentEntryId !== null && visibleIds.includes(currentEntryId)
        ? currentEntryId
        : visibleIds[0] ?? null;

  const focusItem = (id: number): void => {
    setFocusId(id);
    document.getElementById(`${idPrefix}-item-${id}`)?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>): void => {
    const active = document.activeElement as HTMLElement | null;
    const activeId = active?.dataset.galenBrowseTocEntryId;
    if (activeId === undefined) return;
    const id = Number(activeId);
    const idx = visibleIds.indexOf(id);
    const isTop = active?.dataset.galenBrowseTocLevel === 'top';
    const expandedState = active?.getAttribute('aria-expanded');
    let handled = true;
    switch (e.key) {
      case 'ArrowDown':
        if (idx < visibleIds.length - 1) focusItem(visibleIds[idx + 1]!);
        break;
      case 'ArrowUp':
        if (idx > 0) focusItem(visibleIds[idx - 1]!);
        break;
      case 'Home':
        if (visibleIds.length > 0) focusItem(visibleIds[0]!);
        break;
      case 'End':
        if (visibleIds.length > 0) focusItem(visibleIds[visibleIds.length - 1]!);
        break;
      case 'ArrowRight':
        if (isTop && expandedState === 'false') {
          onToggle(id);
        } else if (isTop && expandedState === 'true' && idx < visibleIds.length - 1) {
          focusItem(visibleIds[idx + 1]!);
        }
        break;
      case 'ArrowLeft':
        if (isTop && expandedState === 'true') {
          onToggle(id);
        } else if (!isTop) {
          const rootId = Number(active?.dataset.galenBrowseTocRootOf);
          if (!Number.isNaN(rootId)) focusItem(rootId);
        }
        break;
      default:
        handled = false;
    }
    if (handled) e.preventDefault();
  };

  const renderItem = (
    section: SectionContentEntry,
    level: number,
    indent: number,
    extra: {
      isTop: boolean;
      rootId: number;
      isOpen?: boolean;
      hasChildren?: boolean;
      groupId?: string;
    },
  ): React.ReactElement => {
    const isCurrent = section.sectionId === currentEntryId;
    return (
      <a
        id={`${idPrefix}-item-${section.sectionId}`}
        role="treeitem"
        aria-level={level}
        aria-expanded={extra.isTop && extra.hasChildren ? Boolean(extra.isOpen) : undefined}
        aria-owns={extra.isTop && extra.isOpen ? extra.groupId : undefined}
        aria-current={isCurrent ? 'location' : undefined}
        tabIndex={section.sectionId === tabbableId ? 0 : -1}
        data-galen-browse-toc-entry
        data-galen-browse-toc-entry-id={section.sectionId}
        data-galen-browse-toc-level={extra.isTop ? 'top' : 'sub'}
        data-galen-browse-toc-root-of={extra.isTop ? undefined : extra.rootId}
        href={`#cnt_${section.sectionId}`}
        onFocus={() => setFocusId(section.sectionId)}
        onClick={(e) => {
          e.preventDefault();
          // detail === 0: activated from the keyboard (Enter).
          const via = e.detail === 0 ? 'keyboard' : lastPointer.current;
          onSelect(section.sectionId, via);
        }}
        style={{
          display: 'block',
          flex: 1,
          minWidth: 0,
          margin: 0,
          padding: '0.3rem 0.6rem',
          paddingLeft: tocEntryPadding(indent),
          lineHeight: 1.35,
          borderRadius: '0.25rem',
          textDecoration: 'none',
          color: 'var(--galen-foreground, #0f172a)',
          fontSize: extra.isTop ? '0.9rem' : '0.85rem',
          fontWeight: extra.isTop ? 600 : 400,
          background: isCurrent
            ? 'var(--galen-primary-soft, hsla(199, 89%, 48%, 0.12))'
            : 'transparent',
          boxShadow: isCurrent
            ? 'inset 3px 0 0 var(--galen-primary, hsl(199, 89%, 48%))'
            : 'none',
        }}
      >
        {section.sectionName}
      </a>
    );
  };

  return (
    <ul
      ref={treeRef}
      role="tree"
      aria-label="Guideline contents"
      onKeyDown={onKeyDown}
      onPointerDown={(e) => {
        lastPointer.current = e.pointerType === 'touch' || e.pointerType === 'pen' ? 'touch' : 'mouse';
      }}
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.125rem',
      }}
    >
      {tree.roots.map((root) => {
        const rootId = root.section.sectionId;
        const hasChildren = root.descendants.length > 0;
        const isOpen = hasChildren && expanded.has(rootId);
        const groupId = `${idPrefix}-group-${rootId}`;
        return (
          <li
            key={rootId}
            role="none"
            data-galen-browse-toc-root={rootId}
            style={{ margin: 0, padding: 0 }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              {/* Pointer-only chevron; keyboard users use Right/Left. */}
              <span
                aria-hidden="true"
                data-galen-browse-toc-branch-toggle={hasChildren ? rootId : undefined}
                data-open={hasChildren ? String(isOpen) : undefined}
                onClick={hasChildren ? () => onToggle(rootId) : undefined}
                style={{
                  boxSizing: 'border-box',
                  flexShrink: 0,
                  width: `${TOC_CHEVRON_COLUMN_REM}rem`,
                  minWidth: `${TOC_CHEVRON_COLUMN_REM}rem`,
                  height: '1.9rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: hasChildren ? 'pointer' : 'default',
                  color: 'var(--galen-foreground, #0f172a)',
                  borderRadius: '0.25rem',
                }}
              >
                {hasChildren && <TocChevron open={isOpen} />}
              </span>
              {renderItem(root.section, 1, 0, {
                isTop: true,
                rootId,
                isOpen,
                hasChildren,
                groupId,
              })}
            </div>
            {isOpen && (
              <ul
                id={groupId}
                role="group"
                data-galen-browse-toc-group={rootId}
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.125rem',
                }}
              >
                {root.descendants.map(({ section, indent }) => (
                  <li key={section.sectionId} role="none" style={{ margin: 0, padding: 0 }}>
                    {renderItem(section, 1 + indent, indent, { isTop: false, rootId })}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
};

/* ------------------------------------------------------------------ */
/* Layout: bar + panel (overlay or docked) + guideline body            */
/* ------------------------------------------------------------------ */

export interface BrowseTocLayoutProps {
  sections: SectionContentEntry[];
  /** R28: deepest level listed; resolved per deployment, default 2. */
  depth?: number;
  /** The guideline body (sections + references). */
  children: React.ReactNode;
}

export const BrowseTocLayout: React.FC<BrowseTocLayoutProps> = ({
  sections,
  depth = DEFAULT_BROWSE_TOC_DEPTH,
  children,
}) => {
  const idPrefix = `galen-toc-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const panelId = `${idPrefix}-panel`;

  const tree = useMemo(() => buildTocTree(sections, depth), [sections, depth]);
  const sectionIds = useMemo(() => sections.map((s) => s.sectionId), [sections]);
  const currentId = useCurrentSection(sectionIds);
  const currentEntryId =
    currentId !== null ? tree.displayedEntryOf.get(currentId) ?? null : null;
  const currentRootId = currentId !== null ? tree.rootOf.get(currentId) ?? null : null;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const hotZoneRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const treeRef = useRef<HTMLUListElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusTreeOnOpen = useRef(false);

  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState<boolean>(() => readTocPinned());
  // R29 expansion has two sources (product-owner review of R30):
  //  - ``manual``: branches the reader opened with the chevron / Right
  //    key. Several can stay open until the reader closes them.
  //  - ``autoRoot``: the ONE branch opened because the reader is in it.
  //    It follows the reader - opening another top-level section's
  //    branch closes the previous automatic one - but never closes a
  //    branch the reader opened by hand.
  const [manual, setManual] = useState<ReadonlySet<number>>(() => new Set());
  const [autoRoot, setAutoRoot] = useState<number | null>(null);
  const expanded = useMemo<ReadonlySet<number>>(() => {
    const all = new Set(manual);
    if (autoRoot !== null) all.add(autoRoot);
    return all;
  }, [manual, autoRoot]);
  const width = useElementWidth(rootRef);
  const stuck = useStuck(sentinelRef, barRef);
  // Pinning only means something where the panel can dock; narrower
  // (or unmeasured) widgets get no pin and a stored pin has no effect
  // until the widget is wide enough again.
  const canDock = width !== null && width >= TOC_DOCK_MIN_WIDTH_PX;
  const docked = pinned && canDock;
  const panelVisible = docked || open;
  const focusPinAfterDock = useRef(false);

  useEffect(() => {
    ensureTocPrintRule();
  }, []);

  // R29: a different guideline starts fully collapsed.
  useEffect(() => {
    setManual(new Set());
    setAutoRoot(null);
  }, [sectionIds]);

  // R29: each time the panel appears - and, while it is visible (open or
  // docked), whenever the reader moves into another top-level section -
  // the automatic branch moves to the current section's top level. A
  // branch collapsed by hand stays collapsed until either changes.
  useEffect(() => {
    if (panelVisible) setAutoRoot(currentRootId);
  }, [panelVisible, currentRootId]);

  // Keyboard opening moves focus into the tree once it has rendered.
  // Wait one render while the current section's branch is still being
  // opened, so focus lands on the current entry, not its top level.
  useEffect(() => {
    if (panelVisible && focusTreeOnOpen.current) {
      if (
        currentRootId !== null &&
        currentEntryId !== currentRootId &&
        !expanded.has(currentRootId)
      ) {
        return;
      }
      focusTreeOnOpen.current = false;
      const target = treeRef.current?.querySelector<HTMLElement>('[tabindex="0"]');
      target?.focus();
    }
  });

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  const closePanel = useCallback(
    (returnFocus: boolean) => {
      clearTimers();
      setOpen(false);
      if (returnFocus) buttonRef.current?.focus();
    },
    [clearTimers],
  );

  // Pinning from the overlay swaps it for the docked panel; keep focus
  // on the pin button rather than dropping it to the page.
  useEffect(() => {
    if (docked && focusPinAfterDock.current) {
      focusPinAfterDock.current = false;
      rootRef.current
        ?.querySelector<HTMLElement>('[data-galen-browse-toc-docked] [data-galen-browse-toc-pin]')
        ?.focus();
    }
  }, [docked]);

  // Escape and outside clicks close the overlay (never the docked panel).
  useEffect(() => {
    if (!open || docked) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      const inside = hotZoneRef.current?.contains(document.activeElement) ?? false;
      closePanel(inside);
    };
    const onPointerDown = (e: PointerEvent): void => {
      if (hotZoneRef.current && !hotZoneRef.current.contains(e.target as Node)) {
        closePanel(false);
      }
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, docked, closePanel]);

  const onHotZoneEnter = (e: React.PointerEvent): void => {
    if (e.pointerType !== 'mouse') return; // touch: tap only
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (!open && !docked && !openTimer.current) {
      openTimer.current = setTimeout(() => {
        openTimer.current = null;
        setOpen(true);
      }, TOC_OPEN_DELAY_MS);
    }
  };

  const onHotZoneLeave = (e: React.PointerEvent): void => {
    if (e.pointerType !== 'mouse') return;
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (open && !docked) {
      closeTimer.current = setTimeout(() => {
        closeTimer.current = null;
        setOpen(false);
      }, TOC_CLOSE_DELAY_MS);
    }
  };

  const onButtonClick = (e: React.MouseEvent): void => {
    clearTimers();
    const fromKeyboard = e.detail === 0;
    if (docked) {
      // Already on screen: take keyboard users into it.
      if (fromKeyboard) {
        treeRef.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
      }
      return;
    }
    if (open) {
      setOpen(false);
    } else {
      focusTreeOnOpen.current = fromKeyboard;
      setOpen(true);
    }
  };

  const togglePin = (): void => {
    // Only offered where the panel can dock (canDock).
    const next = !pinned;
    setPinned(next);
    writeTocPinned(next);
    if (!next) {
      // Unpinning puts the panel away, as it would on leave.
      closePanel(true);
    } else {
      // The overlay gives way to the docked column.
      clearTimers();
      setOpen(false);
      focusPinAfterDock.current = true;
    }
  };

  const toggleBranch = useCallback(
    (rootId: number) => {
      if (manual.has(rootId) || autoRoot === rootId) {
        // Collapse, whichever way it was opened.
        setManual((prev) => {
          if (!prev.has(rootId)) return prev;
          const next = new Set(prev);
          next.delete(rootId);
          return next;
        });
        if (autoRoot === rootId) setAutoRoot(null);
      } else {
        // Opened by hand: stays open as the reader moves on.
        setManual((prev) => new Set(prev).add(rootId));
      }
    },
    [manual, autoRoot],
  );

  const onSelect = useCallback(
    (sectionId: number, via: 'mouse' | 'touch' | 'keyboard') => {
      const el = document.getElementById(`cnt_${sectionId}`);
      if (el != null && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
      if (via === 'mouse') return; // stays open while hovered; closes on leave
      if (!docked) closePanel(false);
      if (via === 'keyboard' && el != null) {
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
      }
    },
    [docked, closePanel],
  );

  if (tree.displayedCount === 0) {
    return <>{children}</>;
  }

  // Location: the top-level section only, and only once the bar is
  // stuck - at the top of the guideline it would just repeat the first
  // heading (product-owner review of R30).
  const location =
    stuck && currentRootId !== null
      ? sections.find((s) => s.sectionId === currentRootId)?.sectionName ?? ''
      : '';

  const renderPanel = (mode: 'overlay' | 'docked'): React.ReactElement => (
    <nav
      id={panelId}
      aria-label="Guideline contents"
      data-galen-browse-toc-panel={mode}
      data-galen-browse-toc
      style={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--galen-background, #ffffff)',
        color: 'var(--galen-foreground, #0f172a)',
        fontFamily: 'var(--galen-font-family, system-ui, sans-serif)',
        border: '1px solid var(--galen-border, #e2e8f0)',
        borderRadius: 'var(--galen-border-radius, 0.5rem)',
        ...(mode === 'overlay'
          ? {
              position: 'absolute',
              top: 'calc(100% + 0.35rem)',
              left: 0,
              width: 'min(380px, calc(100vw - 2rem))',
              maxHeight: `calc(100vh - ${OFFSET} - ${BAR_HEIGHT} - 1.5rem)`,
              boxShadow: '0 12px 28px rgba(15, 23, 42, 0.18)',
              zIndex: 21,
            }
          : {
              maxHeight: `calc(100vh - ${OFFSET} - ${BAR_HEIGHT} - 1.5rem)`,
            }),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          padding: '0.5rem 0.5rem 0.5rem 0.9rem',
          borderBottom: '1px solid var(--galen-border, #e2e8f0)',
        }}
      >
        <strong style={{ fontSize: '0.95rem' }}>Contents</strong>
        <span style={{ display: 'inline-flex', gap: '0.25rem' }}>
          {/* Pin only where it can dock; narrow widgets get no pin. */}
          {canDock && (
            <button
              type="button"
              data-galen-browse-toc-pin
              aria-pressed={pinned}
              aria-label={pinned ? 'Unpin contents' : 'Pin contents beside the text'}
              title={pinned ? 'Unpin contents' : 'Pin contents beside the text'}
              onClick={togglePin}
              style={iconButtonStyle(pinned)}
            >
              <PinIcon pinned={pinned} />
            </button>
          )}
          {mode === 'overlay' && (
            <button
              type="button"
              data-galen-browse-toc-close
              aria-label="Close contents"
              title="Close contents"
              onClick={() => closePanel(true)}
              style={iconButtonStyle(false)}
            >
              <span aria-hidden="true" style={{ fontSize: '1.1rem', lineHeight: 1 }}>
                &times;
              </span>
            </button>
          )}
        </span>
      </div>
      <div style={{ overflowY: 'auto', padding: '0.4rem 0.4rem 0.75rem' }}>
        <TocTreeView
          tree={tree}
          expanded={expanded}
          onToggle={toggleBranch}
          currentEntryId={currentEntryId}
          onSelect={onSelect}
          treeRef={treeRef}
          idPrefix={idPrefix}
        />
      </div>
    </nav>
  );

  return (
    <div ref={rootRef} data-galen-browse-toc-layout>
      {/* Stuck detection marker: scrolls normally, sits right above the bar. */}
      <div
        ref={sentinelRef}
        data-galen-browse-toc-sentinel
        aria-hidden="true"
        style={{ height: '1px', marginBottom: '-1px' }}
      />
      <div
        ref={barRef}
        data-galen-browse-toc-bar
        data-stuck={stuck ? 'true' : 'false'}
        style={{
          position: 'sticky',
          top: OFFSET,
          zIndex: 20,
          boxSizing: 'border-box',
          minHeight: BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.4rem 0.25rem',
          margin: '0 0 0.5rem',
          background: 'var(--galen-background, #ffffff)',
          borderBottom: '1px solid var(--galen-border, #e2e8f0)',
        }}
      >
        <div
          ref={hotZoneRef}
          data-galen-browse-toc-hotzone
          onPointerEnter={onHotZoneEnter}
          onPointerLeave={onHotZoneLeave}
          style={{ position: 'relative', flexShrink: 0 }}
        >
          <button
            ref={buttonRef}
            type="button"
            data-galen-browse-toc-toggle
            aria-expanded={panelVisible}
            aria-controls={panelVisible ? panelId : undefined}
            onClick={onButtonClick}
            style={{
              boxSizing: 'border-box',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              margin: 0,
              padding: '0.35rem 0.7rem',
              border: '1px solid var(--galen-border, #e2e8f0)',
              borderRadius: 'var(--galen-border-radius, 0.375rem)',
              background: panelVisible
                ? 'var(--galen-primary-soft, hsla(199, 89%, 48%, 0.12))'
                : 'var(--galen-background, #ffffff)',
              color: 'var(--galen-foreground, #0f172a)',
              cursor: 'pointer',
              font: 'inherit',
              fontSize: '0.85rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <ListIcon />
            Contents
          </button>
          {open && !docked && renderPanel('overlay')}
        </div>
        <span
          data-galen-browse-toc-location
          hidden={!location}
          title={location || undefined}
          style={{
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.85rem',
            color: 'var(--galen-muted, #475569)',
          }}
        >
          {location}
        </span>
      </div>
      <div
        data-galen-browse-toc-columns
        style={
          docked
            ? {
                display: 'grid',
                gridTemplateColumns: '280px minmax(0, 1fr)',
                gap: '1.25rem',
                alignItems: 'start',
              }
            : undefined
        }
      >
        {docked && (
          <aside
            data-galen-browse-toc-docked
            style={{
              position: 'sticky',
              top: `calc(${OFFSET} + ${BAR_HEIGHT} + 0.75rem)`,
              zIndex: 19,
            }}
          >
            {renderPanel('docked')}
          </aside>
        )}
        <div style={{ minWidth: 0 }}>{children}</div>
      </div>
    </div>
  );
};

function iconButtonStyle(active: boolean): React.CSSProperties {
  return {
    boxSizing: 'border-box',
    width: '2rem',
    minWidth: '2rem',
    height: '2rem',
    margin: 0,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: '0.375rem',
    background: active ? 'var(--galen-primary-soft, hsla(199, 89%, 48%, 0.12))' : 'transparent',
    color: active ? 'var(--galen-primary, hsl(199, 89%, 48%))' : 'var(--galen-muted, #475569)',
    cursor: 'pointer',
  };
}
